import { AuthNonceHeader, FillHistoryPage, TradingFillHistory } from '@/api/spec';
import { environment } from '../../environment';
import { useTradeHistoryStore } from '@/feature/terminal_tables/trade_history/TradeHistoryStore';
import { MAX_PAGES_TO_LOAD, MAX_TRADE_MARKS } from './chart_constants';
import useAppStore from '../../store/rootStore';
import { getValidNonce } from '../../store/slices/authSlice';
import { mapFillsToTradeHistory } from '../account/utils/mapFillsToTradeHistory.ts';
import { captureError } from '../../utils/sentryHelpers';

export const fetchTradeHistory = async (
  authNonce: string,
  { cursor, count, signal }: { cursor: string; count: number; signal?: AbortSignal }
): Promise<FillHistoryPage> => {
  const url = new URL(`${environment.apiUrl}${TradingFillHistory}`, window.location.origin);
  if (cursor) url.searchParams.append('page', cursor);
  url.searchParams.append('count', count.toString());

  const response = await fetch(url.toString(), {
    headers: { [AuthNonceHeader]: authNonce },
    signal,
  });
  if (!response.ok) {
    const error: Error & { response?: { status: number } } = new Error(
      `HTTP ${response.status}: Failed to fetch trade history`
    );
    error.response = { status: response.status };
    throw error;
  }
  return (await response.json()) as FillHistoryPage;
};

class TradeHistoryProvider {
  private static instance: TradeHistoryProvider;
  private activeRequest: Promise<any> | null = null;
  private pagesLoaded = 0;

  public static getInstance(): TradeHistoryProvider {
    if (!TradeHistoryProvider.instance) {
      TradeHistoryProvider.instance = new TradeHistoryProvider();
    }
    return TradeHistoryProvider.instance;
  }

  public initialize = (): Promise<void> => {
    return this.runExclusive(async () => {
      if (useTradeHistoryStore.getState().items.length > 0) {
        return;
      }
      useTradeHistoryStore.getState().reset();
      this.pagesLoaded = 0;
      await this.fetchNextPage(true);
    });
  };

  public loadMore = (): Promise<void> => {
    return this.runExclusive(() => this.fetchNextPage());
  };

  public loadUntil = (fromTimestamp: number): Promise<void> => {
    return this.runExclusive(async () => {
      while (true) {
        const state = useTradeHistoryStore.getState();

        if (!state.nextPageCursor || state.items.length >= MAX_TRADE_MARKS || this.pagesLoaded >= MAX_PAGES_TO_LOAD) {
          break;
        }

        const oldestTrade = state.items.length > 0 ? state.items[state.items.length - 1] : null;
        if (oldestTrade && Math.floor(oldestTrade.dateTimestamp / 1000) < fromTimestamp) {
          break;
        }

        // Stop on a page that didn't land. `fetchNextPage` bails out while the store's
        // loading flag is held (it is shared with the table's own paging), and that bail-out
        // advances none of the conditions above — looping on it is a busy `await` that never
        // yields to the event loop, i.e. a pegged CPU and a frozen tab, not a slow chart.
        if (!(await this.fetchNextPage())) {
          break;
        }
      }
    });
  };

  private async runExclusive<T>(loader: () => Promise<T>): Promise<any> {
    if (this.activeRequest) {
      return this.activeRequest;
    }
    this.activeRequest = loader();
    try {
      await this.activeRequest;
    } finally {
      this.activeRequest = null;
    }
  }

  /** @returns whether a page was appended — `false` means no progress was (or can be) made. */
  private async fetchNextPage(isInitial = false): Promise<boolean> {
    const { isHistoryLoading, nextPageCursor, pageSize, items } = useTradeHistoryStore.getState();

    if (isHistoryLoading) return false;
    // Prevent loading more if we're not on the initial load and there's no next page.
    if (!isInitial && !nextPageCursor) return false;
    // Prevent re-initializing if we already have items.
    if (isInitial && items.length > 0) return false;

    useTradeHistoryStore.getState().setIsHistoryLoading(true);

    try {
      const { authNonce, markets } = useAppStore.getState();
      const nonce = getValidNonce(authNonce);
      if (!nonce) {
        throw new Error('Not authorized to fetch trade history');
      }
      const pageData = await fetchTradeHistory(nonce.value, { cursor: nextPageCursor, count: pageSize });

      const mapped = mapFillsToTradeHistory(pageData.d, markets);
      useTradeHistoryStore.getState().addPage(mapped, pageData.np);
      this.pagesLoaded++;
      return true;
    } catch (e) {
      console.error('Failed to fetch next page of trade history', e);
      captureError(e, 'trade_history_fetch');
      useTradeHistoryStore.getState().setIsHistoryLoading(false);
      throw e;
    }
  }
}

export const tradeHistoryProvider = TradeHistoryProvider.getInstance();
